import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthProvider';
import type { Permission, Role } from '../types/v2';
import { V2Client, type V2Transport } from './client';

export interface RegistryEntry {
  id: string;
  name: string;
  section: string;
  definition: string;
  population: string;
  formula?: string;
  source: string;
  period: string;
  reliability: string;
  caveats?: string[];
  replaces?: { id: string; reason: string };
}

interface V2ContextValue {
  /** Null when the dashboard runs on mock data: Analytics 2.0 sections read only from the API. */
  client: V2Transport | null;
  registry: Map<string, RegistryEntry>;
  role: Role | null;
  can: (permission: Permission) => boolean;
}

/** Mirror of api/src/auth.ts. The API checks every request; this table only decides what the interface offers. */
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  admin: ['product.read', 'users.read', 'users.identify', 'retail.read', 'exports.internal', 'exports.commercial', 'exports.audit'],
  analyst: ['product.read', 'users.read', 'retail.read', 'exports.internal'],
  commercial: ['exports.commercial'],
};

const V2Context = createContext<V2ContextValue | null>(null);

export function V2Provider({ children, client: injected }: { children: ReactNode; client?: V2Transport | null }) {
  const { getAccessToken, role } = useAuth();
  const client = useMemo<V2Transport | null>(() => {
    if (injected !== undefined) return injected;
    if (import.meta.env.VITE_ANALYTICS_DATA_SOURCE !== 'api') return null;
    return new V2Client(`${import.meta.env.VITE_ANALYTICS_API_BASE_URL || '/api/analytics'}/v2`, getAccessToken);
  }, [getAccessToken, injected]);
  const [registry, setRegistry] = useState<Map<string, RegistryEntry>>(new Map());

  useEffect(() => {
    if (!client || !role || !ROLE_PERMISSIONS[role].includes('product.read')) return;
    const controller = new AbortController();
    client.get<{ metrics: RegistryEntry[] }>('/registry', {}, controller.signal)
      .then((result) => setRegistry(new Map(result.metrics.map((entry) => [entry.id, entry]))))
      // Without the registry the cards still show values; only the definitions are missing.
      .catch(() => undefined);
    return () => controller.abort();
  }, [client, role]);

  const value = useMemo<V2ContextValue>(() => ({
    client, registry, role, can: (permission) => Boolean(role && ROLE_PERMISSIONS[role].includes(permission)),
  }), [client, registry, role]);
  return <V2Context.Provider value={value}>{children}</V2Context.Provider>;
}

export function useV2Context() {
  const context = useContext(V2Context);
  if (!context) throw new Error('useV2Context must be used inside V2Provider');
  return context;
}
